import { Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AgentEventItem } from '@/components/molecules/AgentEventItem';
import { ChatBubble } from '@/components/molecules/ChatBubble';
import { ToolCallCard } from '@/components/molecules/ToolCallCard';
import { PermissionsContext } from '@/context/Permissions/Permissions.context';
import type { AgentEvent } from '@/services/Conversations';
import { correlateEvents, groupBySpan, hasTrace } from '@/utils/correlateEvents';

import type { TranscriptProps } from './Transcript.types';
import { buildTimeline, hasContent } from './Transcript.utils';

const Toggle = ({
	id,
	checked,
	onChange,
	label,
}: {
	id: string;
	checked: boolean;
	onChange: (v: boolean) => void;
	label: string;
}) => (
	<label htmlFor={id} className="flex cursor-pointer items-center gap-3xs">
		<input
			id={id}
			type="checkbox"
			checked={checked}
			onChange={(e) => onChange(e.target.checked)}
			className="accent-purple-500"
		/>
		{label}
	</label>
);

export const Transcript = ({ messages, events, highlightTraceId }: TranscriptProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'conversationDetail' });
	const [showToolCalls, setShowToolCalls] = useState(true);
	const [showMetadata, setShowMetadata] = useState(false);
	const [showThinking, setShowThinking] = useState(true);

	const visible = messages.filter(hasContent);
	const humans = visible.filter((m) => m.role === 'human').length;
	const ais = visible.filter((m) => m.role === 'ai').length;
	const tools = visible.reduce((acc, m) => acc + (m.toolCalls?.length ?? 0), 0);
	const timeline = buildTimeline(visible, showThinking ? events : [], showToolCalls);
	// Exact join on `toolCallId` (`back#SPEC-010`). No longer positional, so a
	// dropped event costs one panel instead of all of them.
	const related = correlateEvents(events);
	const children = groupBySpan(events);
	// Spans actually present in the timeline. An event whose parent was lost —
	// events are written fire-and-forget — must not vanish from the screen: it
	// renders as a root. Suppressing it just for having a parentSpanId would
	// reintroduce the very failure this spec fixes, and worse: instead of an
	// empty panel, the event would not exist at all.
	const presentSpans = new Set(events.map((e) => e.spanId).filter(Boolean));
	// Children hang off the parent event's span; with no span (an untraced
	// event) there is simply nothing nested to show.
	const childrenOf = (bySpan: Record<string, AgentEvent[]>, parent?: AgentEvent) =>
		parent?.spanId ? bySpan[parent.spanId] : undefined;
	const traced = hasTrace(events);
	// Reads the context directly rather than through `usePermissions` on
	// purpose: that hook throws without a provider, and this organism renders in
	// places that do not mount one. With no verifiable permission we do not
	// claim the user may navigate — the evidence still shows, it just does not
	// link.
	const canNavigateToKnowledge = !!useContext(PermissionsContext)?.permissions?.update;
	// Tool calls belonging to the highlighted turn, resolved by trace rather
	// than by index.
	const highlighted = new Set(
		highlightTraceId
			? events.filter((e) => e.traceId === highlightTraceId && e.toolCallId).map((e) => e.toolCallId as string)
			: [],
	);
	const subtitle =
		events.length > 0 && showThinking
			? t('TIMELINE_SUB_EVENTS', { total: visible.length, events: events.length })
			: t('TRANSCRIPT_SUB', { total: visible.length, humans, ais, tools });

	return (
		<section className="flex flex-col gap-md">
			<header className="flex flex-wrap items-baseline justify-between gap-xs">
				<div>
					<h2 className="text-[15px] font-semibold leading-tight tracking-tight text-default">
						{t('TRANSCRIPT_TITLE')}
					</h2>
					<p className="mt-3xs text-[12px] text-subtle">{subtitle}</p>
					{events.length > 0 && !traced && (
						<p className="mt-3xs text-[11px] text-subtle" data-testid="no-trace-notice">
							{t('NO_TRACE')}
						</p>
					)}
				</div>
				<div className="flex items-center gap-md text-[11px] text-subtle">
					{events.length > 0 && (
						<Toggle id="toggle-thinking" checked={showThinking} onChange={setShowThinking} label={t('SHOW_THINKING')} />
					)}
					<Toggle
						id="toggle-tool-calls"
						checked={showToolCalls}
						onChange={setShowToolCalls}
						label={t('SHOW_TOOL_CALLS')}
					/>
					<Toggle id="toggle-metadata" checked={showMetadata} onChange={setShowMetadata} label={t('SHOW_METADATA')} />
				</div>
			</header>

			<ol className="flex flex-col gap-xs">
				{timeline.map((item) => {
					if (item.kind === 'event') {
						// An event with a parent is drawn **under that parent**, not in its
						// chronological slot: the supervisor's domains run concurrently, so
						// the clock says nothing about what they hang off.
						if (item.event.parentSpanId && presentSpans.has(item.event.parentSpanId)) return null;
						const open = !!highlightTraceId && item.event.traceId === highlightTraceId;

						const kids = item.event.spanId ? children[item.event.spanId] ?? [] : [];

						return (
							<Fragment key={item.key}>
								<AgentEventItem event={item.event} defaultOpen={open} groupRoot={kids.length > 0} />
								{kids.map((child) => (
									<AgentEventItem key={`ev-${child.id}`} event={child} defaultOpen={open} depth={1} />
								))}
							</Fragment>
						);
					}
					if (item.kind === 'tool')
						return (
							<ToolCallCard
								key={item.key}
								tool={item.tool}
								messageId={item.msg.id}
								createdAt={item.msg.createdAt}
								model={item.msg.model}
								relatedEvent={related[item.tool.id]}
								childEvents={childrenOf(children, related[item.tool.id])}
								highlighted={highlighted.has(item.tool.id)}
								canNavigateToKnowledge={canNavigateToKnowledge}
							/>
						);
					return (
						<ChatBubble
							key={item.key}
							msg={item.msg}
							isFirstOfGroup={item.isFirstOfGroup}
							isLastOfGroup={item.isLastOfGroup}
							showMetadata={showMetadata}
						/>
					);
				})}
			</ol>
		</section>
	);
};
